import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AppService } from '../app.service';
import { PermissionsService } from '../permissions/permissions.service';

/**
 * Carries the INVENTORY acting-agency selection to the backend.
 *
 * <p>A platform user shopping in Inventory must pick the agency they are acting for, and that
 * choice has to reach the backend: it decides which agency's SIGN, pricing and markup resolve, and
 * which agency ends up owning the created order.
 *
 * <p>This used to be stamped by {@code devTenantInterceptor}, which is gated on
 * {@code environment.devTenant.enabled}. That flag is false in every environment file we ship, so
 * the header was never actually sent and the backend fell back to the platform user's own
 * `agency_id` claim — the platform pseudo-agency. Every booking a platform user made through the
 * funnel was filed against that pseudo-agency rather than the agency on screen. Acting on behalf of
 * an agency is a product feature, not a dev convenience, so it gets its own interceptor that runs
 * regardless of the bypass.
 *
 * <p>Scope is deliberately narrow:
 * <ul>
 *   <li>Only for platform users. An agency user has no strip, and their token already scopes them;
 *       stamping a header for them could only ever widen their reach.</li>
 *   <li>Only on the inventory surface and on order writes — the funnel. Elsewhere a platform user
 *       keeps their own context.</li>
 *   <li>Never on the order COLLECTION read, which is intentionally cross-agency for platform users.
 *       Detail, voucher and document reads do keep the context, so a selected agency's booking
 *       resolves against that agency.</li>
 *   <li>Never overwrites a header the caller already set.</li>
 * </ul>
 *
 * <p>The backend's `TenantFilter` treats a present `X-Tenant-Agency` as authoritative over the JWT
 * claim, which is exactly what acting-on-behalf-of needs.
 */
export const actingAgencyInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.includes('/api/')) return next(req);
  if (req.headers.has('X-Tenant-Agency')) return next(req);
  if (!isFunnelCall(req)) return next(req);

  // The collection read stays cross-agency for platform users: stamping the acting agency here
  // would silently narrow "all bookings" to one agency.
  if (isOrderCollectionRead(req)) return next(req);

  const perms = inject(PermissionsService, { optional: true });
  if (!perms?.isPlatformAdmin()) return next(req);

  const app = inject(AppService, { optional: true });
  const acting = app?.inventoryAgency();
  if (!acting?.id) return next(req);

  const headers: Record<string, string> = { 'X-Tenant-Agency': acting.id };
  // The name is a display convenience for the backend's cached copy; only sent when we have one,
  // so a missing name never blanks a name the backend already holds.
  if (acting.name && !req.headers.has('X-Tenant-Agency-Name')) {
    headers['X-Tenant-Agency-Name'] = acting.name;
  }
  return next(req.clone({ setHeaders: headers }));
};

/** The inventory surface — search, pricing and the catalogues the funnel reads. */
const INVENTORY_SURFACE = [
  // '/api/v1/hotels' does NOT cover this: the engine's prefix is hotel-engine, with no 's'.
  // Without it a platform user's engine calls carried no acting agency, and the engine — which
  // takes the tenant from the token's agency_id and platform staff have none — answered 401 on
  // every quote and price-preview while shopping worked, shopping being public.
  '/api/v1/hotel-engine',
  '/api/v1/flights', '/api/v1/hotels', '/api/v1/trains', '/api/v1/buses',
  '/api/v1/tours', '/api/v1/ground', '/api/v1/cip', '/api/v1/visas',
  '/api/v1/insurance', '/api/v1/sim', '/api/v1/inventory', '/api/v1/pricing',
  '/api/v1/vouchers', '/api/v1/voucher-review',
];

function isFunnelCall(req: { url: string; method: string }): boolean {
  if (INVENTORY_SURFACE.some((p) => req.url.includes(p))) return true;
  if (!req.url.includes('/api/v1/orders')) return false;
  // The simulator writes orders for whichever agency its own form targets, so it must not be
  // retargeted by the inventory strip.
  if (req.url.includes('/api/v1/orders/simulate')) return false;
  if (req.url.includes('/api/v1/orders/sim-batch')) return false;
  return true;
}

function isOrderCollectionRead(req: { url: string; method: string }): boolean {
  if (req.method !== 'GET') return false;
  const path = req.url.split('?')[0].replace(/\/+$/, '');
  return path.endsWith('/api/v1/orders');
}
